import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { X, Trash2, LogOut, Sparkles, Eraser, Database, Layers } from 'lucide-react'
import { useSettingsStore } from '@/state/useSettingsStore'
import { useViewStore } from '@/state/useViewStore'
import { useAuth } from '@/auth/AuthContext'
import { useTrophyStore } from '@/state/useTrophyStore'
import { useDashboardStore } from '@/state/useDashboardStore'
import {
  useBumpCategoryValue,
  useClearSnapshot,
  useLoadMockSnapshot,
} from '@/data/DataServiceContext'
import { ThemeSwitcher } from '@/components/controls/ThemeSwitcher'
import { LanguageSwitcher } from '@/components/controls/LanguageSwitcher'
import { useTenant } from '@/tenant/TenantProvider'
import clsx from 'clsx'
import { unlockAudio } from '@/lib/soundManager'
import { useNavigate } from 'react-router-dom'
import type { Category, EvolutionTier } from '@/milestones/types'
import { getRingTone } from '@/components/trophies/RankRing'
import { DEFAULT_MILESTONES } from '@/milestones/definitions'
import { TIER_MAPPINGS } from '@/milestones/evolution'
import { appBus } from '@/lib/eventBus'
import { celebrationQueue } from '@/components/celebration/celebrationQueue'
import { isProd } from '@/lib/env'

const VERSION = '0.1.0'

export function SettingsOverlay() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const pause = useViewStore((s) => s.pause)
  const resume = useViewStore((s) => s.resume)
  const settings = useSettingsStore()
  const tenant = useTenant()
  const trophyReset = useTrophyStore((s) => s.reset)
  const { logout, session } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      )
        return
      if (e.key === 'Escape' && open) {
        setOpen(false)
        resume()
      } else if (e.key.toLowerCase() === 's' && !open) {
        setOpen(true)
        pause('settings')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, pause, resume])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('settings') === '1') {
      setOpen(true)
      pause('settings')
    }
  }, [pause])

  function close() {
    setOpen(false)
    resume()
  }

  async function onLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-40 grid place-items-center bg-black/50 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl border border-border bg-surface-1/95 p-7 shadow-2xl"
          >
            <button
              type="button"
              onClick={close}
              aria-label={t('settings.close')}
              className="absolute right-5 top-5 grid size-9 place-items-center rounded-full bg-surface-2/80 text-ink-muted transition hover:text-ink"
            >
              <X className="size-4" />
            </button>
            <h2 className="font-display text-2xl font-semibold text-ink">{t('settings.title')}</h2>
            <p className="mt-1 text-sm text-ink-muted">
              {session?.email} · <span className="text-ink-subtle">{tenant.name}</span>
            </p>

            <div className="mt-6 space-y-5">
              <Row label={t('settings.theme')}>
                <ThemeSwitcher />
              </Row>
              <Row label={t('settings.language')}>
                <LanguageSwitcher />
              </Row>
              <Row label={t('settings.slideInterval')}>
                <NumberStepper
                  value={settings.slideIntervalSec ?? 0}
                  placeholder="auto"
                  min={0}
                  max={180}
                  step={5}
                  onChange={(v) => settings.setSlideIntervalSec(v === 0 ? null : v)}
                />
              </Row>
              <Row label={t('settings.soundVolume')}>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={settings.soundVolume}
                  onChange={(e) => settings.setSoundVolume(Number(e.target.value))}
                  className="w-full accent-[color:var(--brand-primary)]"
                  onPointerDown={() => void unlockAudio()}
                />
              </Row>
              <Row label={t('settings.audioEnabled')}>
                <Toggle
                  checked={settings.soundEnabled}
                  onChange={(v) => {
                    settings.setSoundEnabled(v)
                    if (v) void unlockAudio()
                  }}
                />
              </Row>
              <Row label={t('settings.quietHours')}>
                <div className="flex items-center gap-2 text-xs text-ink-muted">
                  <span>{t('settings.quietFrom')}</span>
                  <HourSelect
                    value={settings.quietHoursFrom}
                    onChange={(v) => settings.setQuietHours(v, settings.quietHoursTo)}
                  />
                  <span>—</span>
                  <span>{t('settings.quietTo')}</span>
                  <HourSelect
                    value={settings.quietHoursTo}
                    onChange={(v) => settings.setQuietHours(settings.quietHoursFrom, v)}
                  />
                </div>
              </Row>
              <Row label={t('settings.resetTrophies')}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--signal-bad)]/40 px-3 py-1 text-xs text-[color:var(--signal-bad)] transition hover:bg-[color:var(--signal-bad)]/10"
                  onClick={() => {
                    if (window.confirm(t('settings.resetTrophiesConfirm'))) trophyReset()
                  }}
                >
                  <Trash2 className="size-3.5" />
                  {t('settings.resetTrophies')}
                </button>
              </Row>
              <Row label={t('settings.logout')}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs text-ink-muted transition hover:border-brand-primary/40 hover:text-ink"
                  onClick={onLogout}
                >
                  <LogOut className="size-3.5" />
                  {t('settings.logout')}
                </button>
              </Row>
            </div>

            {!isProd && <DebugTierSection />}

            <div className="mt-6 border-t border-border pt-4 text-xs text-ink-subtle">
              {t('settings.version')} {VERSION} · {t(`header.dataSource.${tenant.api.dataSource}`)}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="min-w-0 flex-1 text-sm text-ink-muted">{label}</span>
      <div className="flex shrink-0 items-center justify-end">{children}</div>
    </div>
  )
}

function NumberStepper({
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
}: {
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step?: number
  placeholder?: string
}) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - step))}
        className="size-7 rounded-full border border-border bg-surface-2/70 text-ink-muted hover:text-ink"
      >
        −
      </button>
      <span className="min-w-12 text-center font-mono text-sm text-ink">
        {value === 0 && placeholder ? placeholder : value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + step))}
        className="size-7 rounded-full border border-border bg-surface-2/70 text-ink-muted hover:text-ink"
      >
        +
      </button>
    </div>
  )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-6 w-10 items-center rounded-full transition',
        checked ? 'bg-brand-primary' : 'bg-surface-3',
      )}
    >
      <span
        className={clsx(
          'inline-block size-4 rounded-full bg-white shadow transition',
          checked ? 'translate-x-5' : 'translate-x-1',
        )}
      />
    </button>
  )
}

function HourSelect({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="rounded-md border border-border bg-surface-2 px-2 py-1 text-sm text-ink"
    >
      {Array.from({ length: 24 }, (_, i) => (
        <option key={i} value={i}>
          {String(i).padStart(2, '0')}:00
        </option>
      ))}
    </select>
  )
}

const CATEGORIES: Category[] = ['growth', 'revenue', 'reliability', 'speed']
const TIER_NAMES_CS: Record<EvolutionTier, string> = {
  0: 'Železo',
  1: 'Bronz',
  2: 'Stříbro',
  3: 'Zlato',
  4: 'Platina',
  5: 'Diamant',
}
const TIER_NAMES_EN: Record<EvolutionTier, string> = {
  0: 'Iron',
  1: 'Bronze',
  2: 'Silver',
  3: 'Gold',
  4: 'Platinum',
  5: 'Diamond',
}

function DebugTierSection() {
  const { t } = useTranslation()
  const locale = useSettingsStore((s) => s.locale)
  const debugTiers = useSettingsStore((s) => s.debugTiers)
  const setDebugTier = useSettingsStore((s) => s.setDebugTier)
  const clearDebugTiers = useSettingsStore((s) => s.clearDebugTiers)
  const tierNames = locale === 'cs' ? TIER_NAMES_CS : TIER_NAMES_EN

  function previewCelebration(cat: Category, tier: EvolutionTier) {
    const def = DEFAULT_MILESTONES.find((d) => d.category === cat)
    if (!def) return
    celebrationQueue.push({
      id: `preview:${cat}:${tier}:${Date.now()}`,
      trophy: { milestoneId: def.id, hitAt: new Date().toISOString() },
      def,
      newTier: tier,
    })
    void appBus
  }

  return (
    <div className="mt-6 space-y-3 border-t border-border pt-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-semibold text-ink">
            {locale === 'cs' ? 'Debug · ranky' : 'Debug · ranks'}
          </h3>
          <p className="text-xs text-ink-subtle">
            {locale === 'cs'
              ? 'Ručně přepni tier pro každou kategorii — pro debug a vizuální kontrolu divizí Železo–Diamant. Override má přednost před skutečným tierem ze získaných milníků.'
              : 'Manually override the tier per category — for debugging and reviewing Iron–Diamond divisions visually. Override takes priority over the real tier from unlocked milestones.'}
          </p>
        </div>
        <button
          type="button"
          onClick={clearDebugTiers}
          disabled={Object.keys(debugTiers).length === 0}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-ink-muted transition hover:border-brand-accent/40 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Eraser className="size-3" />
          {locale === 'cs' ? 'Zrušit override' : 'Clear override'}
        </button>
      </div>

      <div className="space-y-2">
        {CATEGORIES.map((cat) => (
          <div key={cat} className="flex items-center gap-3">
            <span className="w-28 shrink-0 truncate text-sm text-ink-muted">
              {t(`milestones.category.${cat}`)}
            </span>
            <div className="flex flex-1 flex-wrap items-center gap-1">
              {([0, 1, 2, 3, 4, 5] as EvolutionTier[]).map((tier) => {
                const active = debugTiers[cat] === tier
                const tone = getRingTone(tier)
                return (
                  <button
                    key={tier}
                    type="button"
                    onClick={() => setDebugTier(cat, active ? null : tier)}
                    title={tierNames[tier]}
                    className={clsx(
                      'relative inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest transition',
                      active
                        ? 'border-transparent text-[#0a0a18] shadow'
                        : 'border-border bg-surface-2/60 text-ink-muted hover:text-ink',
                    )}
                    style={
                      active
                        ? {
                            background: `linear-gradient(135deg, ${tone.highlight}, ${tone.outer}, ${tone.shadow})`,
                          }
                        : undefined
                    }
                  >
                    T{tier}
                  </button>
                )
              })}
              <button
                type="button"
                onClick={() => {
                  const t = debugTiers[cat] ?? 1
                  previewCelebration(cat, t as EvolutionTier)
                }}
                title={
                  locale === 'cs' ? 'Spustit ukázkovou celebration' : 'Trigger preview celebration'
                }
                className="ml-1 inline-flex items-center gap-1 rounded-full border border-brand-accent/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-accent transition hover:bg-brand-accent/10"
              >
                <Sparkles className="size-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <DataSeedSection />
      <BumpMetricSection />
    </div>
  )
}

function DataSeedSection() {
  const locale = useSettingsStore((s) => s.locale)
  const loadMock = useLoadMockSnapshot()
  const clear = useClearSnapshot()
  return (
    <div className="mt-5 space-y-2 border-t border-border pt-4">
      <div>
        <h4 className="text-sm font-semibold text-ink">
          {locale === 'cs' ? 'Data dashboard (debug)' : 'Dashboard data (debug)'}
        </h4>
        <p className="text-[11px] text-ink-subtle">
          {locale === 'cs'
            ? 'Naplň mockovými daty pro ukázku „jak to vypadá rozjeté", nebo vyčisti zpět do start-stavu (0 všude + reset trofejí).'
            : 'Load mock data to preview the populated dashboard, or clear back to the zero start state (0 everywhere + reset trophies).'}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={loadMock}
          className="inline-flex items-center gap-1.5 rounded-full border border-brand-primary/40 bg-brand-primary/10 px-3 py-1.5 text-xs font-medium text-brand-primary transition hover:bg-brand-primary/20"
        >
          <Database className="size-3.5" />
          {locale === 'cs' ? 'Naplnit mock daty' : 'Fill with mock data'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (
              window.confirm(
                locale === 'cs' ? 'Vymazat data + reset trofejí?' : 'Clear data + reset trophies?',
              )
            )
              clear()
          }}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-ink-muted transition hover:border-[color:var(--signal-bad)]/40 hover:text-[color:var(--signal-bad)]"
        >
          <Layers className="size-3.5" />
          {locale === 'cs' ? 'Vyčistit data' : 'Clear data'}
        </button>
      </div>
    </div>
  )
}

function BumpMetricSection() {
  const locale = useSettingsStore((s) => s.locale)
  const snapshot = useDashboardStore((s) => s.snapshot)
  const bumpCategoryValue = useBumpCategoryValue()
  const { t } = useTranslation()

  function bump(cat: Category, delta: number) {
    void unlockAudio()
    bumpCategoryValue(cat, delta)
  }

  return (
    <div className="mt-5 space-y-2 border-t border-border pt-4">
      <div>
        <h4 className="text-sm font-semibold text-ink">
          {locale === 'cs' ? 'Přidat hodnoty (debug)' : 'Bump metric (debug)'}
        </h4>
        <p className="text-[11px] text-ink-subtle">
          {locale === 'cs'
            ? 'Mění hodnotu primární metriky kategorie. Tier se přepočítá automaticky, milníky se odpálí (konfety + zvuk).'
            : 'Adjusts the primary metric of each category. Tier recomputes, milestones trigger (confetti + sound).'}
        </p>
      </div>
      <div className="space-y-1.5">
        {CATEGORIES.map((cat) => {
          const m = TIER_MAPPINGS[cat]
          const cur = snapshot ? m.getValue(snapshot) : 0
          const tone = getRingTone(0)
          void tone
          return (
            <div key={cat} className="flex items-center gap-2">
              <span className="w-28 shrink-0 truncate text-xs text-ink-muted">
                {t(`milestones.category.${cat}`)}
              </span>
              <button
                type="button"
                onClick={() => bump(cat, -m.milestoneStep)}
                className="inline-flex size-7 items-center justify-center rounded-full border border-border bg-surface-2/70 font-mono text-base text-ink-muted hover:text-ink"
                title={`−${formatStepLabel(m.milestoneStep, cat, locale)}`}
              >
                −
              </button>
              <span className="min-w-20 text-center font-mono text-xs tabular-nums text-ink">
                {formatMetricValue(cur, cat, locale)}
              </span>
              <button
                type="button"
                onClick={() => bump(cat, m.milestoneStep)}
                className="inline-flex size-7 items-center justify-center rounded-full border border-brand-primary/40 bg-brand-primary/10 font-mono text-base text-brand-primary hover:bg-brand-primary/20"
                title={`+${formatStepLabel(m.milestoneStep, cat, locale)}`}
              >
                +
              </button>
              <span className="text-[10px] uppercase tracking-widest text-ink-subtle">
                {formatStepLabel(m.milestoneStep, cat, locale)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatStepLabel(step: number, category: Category, locale: 'cs' | 'en'): string {
  switch (category) {
    case 'growth':
      return `${step}`
    case 'revenue':
      return `${step / 1000} k Kč`
    case 'reliability':
      return locale === 'cs' ? `${step} d` : `${step} d`
    case 'speed':
      return `${step}`
  }
}

function formatMetricValue(value: number, category: Category, locale: 'cs' | 'en'): string {
  switch (category) {
    case 'growth':
      return value.toLocaleString(locale === 'cs' ? 'cs-CZ' : 'en-GB')
    case 'revenue': {
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} M`
      if (value >= 1000) return `${Math.round(value / 1000)} k`
      return `${value}`
    }
    case 'reliability':
      return `${value} d`
    case 'speed':
      return value.toLocaleString(locale === 'cs' ? 'cs-CZ' : 'en-GB')
  }
}
